import { Agency, AgencySubtypeId, AgencySybTypeId } from '@app/models/agency';
import { AgencyTypeId } from '@app/models/agency-type-id';
import { safeProp } from '@util/object';
import { hasValue, isTrue } from '@util/type';
import * as R from 'ramda';

export const getId = R.prop('id');
type CheckAgency = (a: Agency | AgencyTypeId) => boolean;
export const isApplicantAgency: CheckAgency = R.prop('applicant');
export const isImAgency: CheckAgency = R.prop('im');
export const isCossAgency: CheckAgency = R.prop('coss');
export const isRuAgency: CheckAgency = R.prop('ru');
export const isProductionAgency: CheckAgency = R.prop('production');
export const isServiceAgency: CheckAgency = R.prop('service');
export const isAdminAgency: CheckAgency = R.prop('admin');
export const isUnknownAgency: CheckAgency = R.prop('unknownAgency');
export const isKnownAgency: CheckAgency = R.complement(isUnknownAgency);

const isKnownApplicantAgency: CheckAgency = R.allPass([
  isApplicantAgency,
  isKnownAgency
]);

const isKnownProductionAgency = R.allPass([
  isProductionAgency,
  isKnownAgency
]);

type FilterAgencies = (agencies: Agency[]) => Agency[];
export const filterApplicantAgencies: FilterAgencies = R.filter(isApplicantAgency);
export const filterServiceAgencies: FilterAgencies = R.filter(isServiceAgency);
export const filterProductionAgencies: FilterAgencies = R.filter(isProductionAgency);
export const filterRfcAgencies: FilterAgencies = R.filter(isCossAgency);

type GetDefaultAgencySubType = (agency: Agency) => AgencySubtypeId;
export const getDefaultAgencySubType: GetDefaultAgencySubType = R.prop('defaultAgencySubTypeId');
type GetDefaultAgencySubTypeId = (agency: Agency) => string;
export const getDefaultAgencySubTypeId: GetDefaultAgencySubTypeId = R.compose(getId, getDefaultAgencySubType);
type GetAgencySubTypes = (agency: Agency) => AgencySubtypeId[];
export const getAgencySubTypes: GetAgencySubTypes = R.prop('agencySubTypes');
type MapAgencySubTypeIds = (agencySubTypes: AgencySubtypeId[]) => string[];
export const mapAgencySubTypesById: MapAgencySubTypeIds = R.ifElse(
  hasValue,
  (agencySubTypes) => R.map(getId, agencySubTypes),
  R.always([])
);

type GetAgencySubTypeIds = (agency: Agency) => string[];
export const getAgencySubTypeIds: GetAgencySubTypeIds = R.compose(mapAgencySubTypesById, getAgencySubTypes);

export const containsAgencySubTypeId = (agencySubTypeId: AgencySubtypeId) => R.compose(R.includes(agencySubTypeId), getAgencySubTypes);
export const containsAnyAgencySubTypeId = (agencySubTypeIds: AgencySubtypeId[]) => R.compose(
  R.any((agencySubTypeId: AgencySubtypeId) => R.includes(agencySubTypeId, agencySubTypeIds)),
  getAgencySubTypes
);
export const containsAgencySubTypeIdWithId = (id: string) => R.compose(R.includes(id), getAgencySubTypeIds);
export const containsAnyAgencySubTypeIdWithId = (agencySubTypeIds: string[]) => R.compose(
  R.any((id: string) => R.includes(id, agencySubTypeIds)),
  getAgencySubTypeIds
);
const isByDefault = (agencySubTypeId: string) => R.compose(R.equals(agencySubTypeId), safeProp('id'), getDefaultAgencySubType);
export const isPassengerDefaultAgency = isByDefault(AgencySybTypeId.Passenger);
export const isCargoDefaultAgency = isByDefault(AgencySybTypeId.Cargo);
export const isOssDefaultAgency = isByDefault(AgencySybTypeId.Oss);

export const isPassengerAgency = containsAgencySubTypeIdWithId(AgencySybTypeId.Passenger);
export const isCargoAgency = containsAgencySubTypeIdWithId(AgencySybTypeId.Cargo);
export const isApplicantOssAgency = R.allPass([
  isApplicantAgency,
  containsAgencySubTypeIdWithId(AgencySybTypeId.Oss)
]);
export const isPassengerApplicantAgency = R.allPass([
  isKnownApplicantAgency,
  isPassengerAgency
]);

const isPassengerApplicantOrProductionAgency = R.anyPass([
  isPassengerApplicantAgency,
  isKnownProductionAgency
]);

export const filterPassengerApplicantAndProductionAgencies: FilterAgencies = R.filter(isPassengerApplicantOrProductionAgency);

type SortByAgencyName = (agencies: Agency[]) => Agency[];
export const sortByAgencyName: SortByAgencyName = R.sortBy(R.prop('name'));

export const APPLICANT_AGENCY_TYPE = 'APPLICANT';
export const IM_AGENCY_TYPE = 'IM';
export const RU_AGENCY_TYPE = 'RU';
export const COSS_AGENCY_TYPE = 'COSS';
export const FEASIBILITY_STUDY_AGENCY_TYPE = 'FS';
export const PROD_AGENCY_TYPE = 'PRODUCTION';
export const SERVICE_AGENCY_TYPE = 'SERVICE';
export const ADMIN_AGENCY_TYPE = 'ADMIN';

type GetHiddenTrainCompositionColumns = (agency: Agency) => string[];
export const getHiddenTrainCompositionColumns: GetHiddenTrainCompositionColumns =
  R.path(['agencyDisplaySettings', 'hiddenTrainCompositionColumns']);

type AddAgencyDisplaySettingsEntry = (agency: Agency, hiddenColumnsNames: string[]) => Agency;
export const addHiddenTrainCompositionColumns: AddAgencyDisplaySettingsEntry = (agency: Agency, hiddenColumnsNames: string[]) =>
  R.mergeDeepRight(agency, {
    agencyDisplaySettings: {
      hiddenTrainCompositionColumns: hiddenColumnsNames
    }
  }) as Agency;

type GetAgencyTypeText = (agencyType: string) => string;
export const getAgencyTypeText: GetAgencyTypeText = R.cond([
  [R.equals(APPLICANT_AGENCY_TYPE), R.always('control.evu')],
  [R.equals(IM_AGENCY_TYPE), R.always('control.km')],
  [R.equals(COSS_AGENCY_TYPE), R.always('control.coss')],
  [R.equals(FEASIBILITY_STUDY_AGENCY_TYPE), R.always('control.fs')]
]);

export const APPLICANT_AGENCY_TYPE_ID = 'EVU';
export const IM_AGENCY_TYPE_ID = 'KM';
export const RU_AGENCY_TYPE_ID = 'RU';
export const COSS_AGENCY_TYPE_ID = 'C-OSS';
export const FEASIBILITY_STUDY_AGENCY_TYPE_ID = 'FS';
export const PROD_AGENCY_TYPE_ID = 'PROD';
export const SERVICE_AGENCY_TYPE_ID = 'SRV';
export const ADMIN_AGENCY_TYPE_ID = 'ADM';

export const AgencyTypes = {
  [APPLICANT_AGENCY_TYPE]: {id: APPLICANT_AGENCY_TYPE_ID, description: 'control.evu'},
  [IM_AGENCY_TYPE]: {id: IM_AGENCY_TYPE_ID, description: 'control.km'},
  [RU_AGENCY_TYPE]: {id: RU_AGENCY_TYPE_ID, description: 'control.ru'},
  [COSS_AGENCY_TYPE]: {id: COSS_AGENCY_TYPE_ID, description: 'control.coss'},
  [FEASIBILITY_STUDY_AGENCY_TYPE]: {id: FEASIBILITY_STUDY_AGENCY_TYPE_ID, description: 'control.fs'},
  [PROD_AGENCY_TYPE]: {id: PROD_AGENCY_TYPE_ID, description: 'control.prod'},
  [SERVICE_AGENCY_TYPE]: {id: SERVICE_AGENCY_TYPE_ID, description: 'control.srv'},
  [ADMIN_AGENCY_TYPE]: {id: ADMIN_AGENCY_TYPE_ID, description: 'control.adm'}
};

type GetAgencyType = (agencyType: any) => AgencyTypeId;
export const getAgencyType: GetAgencyType = R.cond([
  [R.equals(APPLICANT_AGENCY_TYPE), R.always(AgencyTypes.APPLICANT)],
  [R.equals(IM_AGENCY_TYPE), R.always(AgencyTypes.IM)],
  [R.equals(COSS_AGENCY_TYPE), R.always(AgencyTypes[COSS_AGENCY_TYPE])],
  [R.equals(FEASIBILITY_STUDY_AGENCY_TYPE), R.always(AgencyTypes[FEASIBILITY_STUDY_AGENCY_TYPE])]
]);

type ResolveAgencyType = (agency: Agency) => AgencyTypeId;
export const resolveAgencyType: ResolveAgencyType = R.cond([
  [R.compose(isTrue, isApplicantAgency), R.always(AgencyTypes.APPLICANT)],
  [R.compose(isTrue, isImAgency), R.always(AgencyTypes.IM)],
  [R.compose(isTrue, isCossAgency), R.always(AgencyTypes.COSS)],
  [R.compose(isTrue, isRuAgency), R.always(AgencyTypes.RU)],
  [R.compose(isTrue, isProductionAgency), R.always(AgencyTypes.PRODUCTION)],
  [R.compose(isTrue, isServiceAgency), R.always(AgencyTypes.SERVICE)],
  [R.compose(isTrue, isAdminAgency), R.always(AgencyTypes.ADMIN)]
]);

const isAgencyTypeIdEqualTo = (agencyTypeId) => R.compose(R.equals(agencyTypeId), getId);
export const isApplicantAgencyTypeId = isAgencyTypeIdEqualTo(APPLICANT_AGENCY_TYPE_ID);
export const isImAgencyTypeId = isAgencyTypeIdEqualTo(IM_AGENCY_TYPE_ID);
export const isCossAgencyTypeId = isAgencyTypeIdEqualTo(COSS_AGENCY_TYPE_ID);
export const isFeasibilityStudyAgencyTypeId = isAgencyTypeIdEqualTo(FEASIBILITY_STUDY_AGENCY_TYPE_ID);
export const isRuAgencyTypeId = isAgencyTypeIdEqualTo(RU_AGENCY_TYPE_ID);
export const isProductionAgencyTypeId = isAgencyTypeIdEqualTo(PROD_AGENCY_TYPE_ID);
export const isServiceAgencyTypeId = isAgencyTypeIdEqualTo(SERVICE_AGENCY_TYPE_ID);
export const isAdminAgencyTypeId = isAgencyTypeIdEqualTo(ADMIN_AGENCY_TYPE_ID);

type ResolveAgencySubTypeIds = (agencyTypeId: AgencyTypeId) => string[];
export const resolveAgencySubTypeIds: ResolveAgencySubTypeIds = R.cond([
  [isApplicantAgencyTypeId, R.always([AgencySybTypeId.Cargo, AgencySybTypeId.Passenger, AgencySybTypeId.Oss])],
  [isImAgencyTypeId, R.always([AgencySybTypeId.Infra])],
  [isCossAgencyTypeId, R.always([AgencySybTypeId.Oss])],
  [isRuAgencyTypeId, R.always([AgencySybTypeId.Cargo, AgencySybTypeId.Passenger])],
  [isProductionAgencyTypeId, R.always([AgencySybTypeId.Cargo, AgencySybTypeId.Passenger])],
  [isServiceAgencyTypeId, R.always([AgencySybTypeId.Passenger])],
  [isAdminAgencyTypeId, R.always([])]
]);

export const getAgencyTypeTimetableKey = R.cond([
  [isApplicantAgencyTypeId, R.always('label.dossier.timetable.applicant')],
  [isImAgencyTypeId, R.always('label.dossier.timetable.im')],
  [isCossAgencyTypeId, R.always('label.dossier.timetable.coss')],
  [isFeasibilityStudyAgencyTypeId, R.always('timetable.fs.title')]
]);

type GetShortName = (agency: Agency) => string;
export const getShortName: GetShortName = <GetShortName>safeProp('shortName');
